import { Fragment, PropsWithChildren } from "react";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
} from "@headlessui/react";
import { classes } from "lib/actions/util";
import CloseButton from "components/elements/Button/CloseButton";

export default function Modal(
  p: PropsWithChildren<{
    isOpen: boolean;
    isMinimized?: boolean;
    className?: string;
    disableStyling?: boolean;
    disableSizing?: boolean;
    onClose?: () => void;
    onEscape?: () => void;
    initialFocusRef?: React.MutableRefObject<HTMLElement | null>;
    zIndex?: number;
  }>,
) {
  return (
    <Transition
      show={p.isOpen && !p.isMinimized}
      unmount={p.isMinimized == undefined}
      appear
    >
      <Dialog
        className={classes("relative", `z-${p.zIndex ?? 10}`)}
        onClose={() => p.onEscape?.()}
        unmount={false}
        initialFocus={p.initialFocusRef}
        // Note: Stop drag & drop from escaping modal to prevent
        // document (hence FileDropzoneFullScreen) from seeing them.
        onDragEnter={(ev) => ev.stopPropagation()}
        onDragOver={(ev) => ev.stopPropagation()}
        onDragLeave={(ev) => ev.stopPropagation()}
        onDrop={(ev) => ev.stopPropagation()}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="
              fixed inset-0
              bg-gray-500 bg-opacity-20 
              backdrop-blur-sm backdrop-filter"
          />
        </TransitionChild>

        <div className="fixed inset-0 mr-4 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 pr-0">
            <TransitionChild
              as={Fragment}
              unmount={false}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={classes(
                  "relative",
                  !p.disableStyling && "rounded-2xl bg-white shadow-xl",
                  !p.disableSizing && "w-full sm:max-w-xl",
                  p.className,
                )}
              >
                {p.onClose && (
                  <CloseButton
                    className="absolute right-3 top-3 p-2"
                    onClick={p.onClose}
                  />
                )}

                {p.children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
